import type { ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * Step back, step forward, and whatever belongs between them.
 *
 * It used to read "← Previous | Today | Next →", three words where two arrows do. The Today button
 * went because the nav bar already has a Today tab one row above it — pressing either did the same
 * thing, and a duplicate control is a thing to read past rather than a second way in. Screens that
 * are *not* Today keep it, since there is nothing else there to return them to now.
 *
 * The middle is a slot rather than a fixed control: the day screen puts its date picker there, so
 * "jump to a date" and "step a day" stop being two separate clusters on one row.
 */
export function PeriodNav({
  onPrevious,
  onNext,
  onToday,
  label,
  disabled = false,
  children,
}: {
  onPrevious: () => void;
  onNext: () => void;
  /** Offered only when nothing else occupies the middle. */
  onToday?: () => void;
  /** What is being stepped — "day", "week" — so the arrows say what they do to a screen reader. */
  label: string;
  disabled?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="period-nav">
      <button
        type="button"
        className="icon-button step"
        disabled={disabled}
        aria-label={`Previous ${label}`}
        title={`Previous ${label}`}
        onClick={onPrevious}
      >
        <Icon name="caret" size={16} />
      </button>

      {children ??
        (onToday && (
          <button type="button" className="ghost" disabled={disabled} onClick={onToday}>
            Today
          </button>
        ))}

      <button
        type="button"
        className="icon-button step next"
        disabled={disabled}
        aria-label={`Next ${label}`}
        title={`Next ${label}`}
        onClick={onNext}
      >
        <Icon name="caret" size={16} />
      </button>
    </div>
  );
}
